import {
  AvatarGroup,
  AvatarGroupItem,
  AvatarGroupProps,
  AvatarNamedColor,
  mergeClasses,
} from '@fluentui/react-components';

import { ImageIcon } from 'src/ui-models/Icon';

const MAX_INLINE_ITEMS = 4;

/**
 * Props for IconGroup component
 */

export interface IconGroupProps extends AvatarGroupProps {
  /**
   * List of icons to render
   */
  items: ImageIcon[]
  /**
   * Maximum number of inline items that can be displayed includes an indicator of "+X."
   */
  maxInlineItems?: number
  /**
   * Background color of an icon
   */
  iconBgColor?: 'neutral' | 'brand' | 'colorful' | AvatarNamedColor
}

/**
 * Get the inline and rest items based on the list of Icon needed for IconGroup.
 *
 * @param items - List of icons to render
 * @param maxInlineCount - Maximum number of inline items that can be displayed includes an indicator of "+X."
 * @returns Two arrays split into inlineItems and restItems based on maxInlineItems.
 */
export const partitionIconGroupItems = (
  items: ImageIcon[],
  maxInlineCount = MAX_INLINE_ITEMS,
): { inlineItems: ImageIcon[], restItems: ImageIcon[] } => {
  if (!items.length) {
    return { inlineItems: [], restItems: [] };
  }

  const inlineCount = maxInlineCount - (items.length > maxInlineCount ? 1 : 0);
  const inlineItems = items.slice(0, inlineCount);
  const restItems = items.slice(inlineCount, items.length);
  return { inlineItems, restItems };
};

const IconGroup: React.FC<IconGroupProps> = (props: IconGroupProps) => {
  const { items, maxInlineItems = MAX_INLINE_ITEMS, iconBgColor = 'neutral' } = props;
  const { inlineItems, restItems } = partitionIconGroupItems(items, maxInlineItems);

  const renderedAddMore = items.length === 0 && <AvatarGroupItem initials="+" color={iconBgColor} />;
  const renderedInlineItems = inlineItems.map(item => (
    <AvatarGroupItem key={item.name} name={item.name} color={iconBgColor} image={{ src: item.imgUrl }} />
  ));
  const renderedRestItemsCount = restItems.length > 0 && (
    <AvatarGroupItem initials={`+${restItems.length}`} color={iconBgColor} />
  );

  return (
    <AvatarGroup className={mergeClasses('icon-group', props.className)} layout="stack">
      {renderedAddMore}

      {renderedInlineItems}

      {renderedRestItemsCount}
    </AvatarGroup>
  );
};

export default IconGroup;
